// skipcq: JS-W1028

export const FIRST_TOUCH_SORT_COLUMNS = {
  name: 'name',
  entityType: 'entityType',
  city: 'city',
  state: 'state',
  userCount: 'userCount',
  earliestAcquisition: 'earliestAcquisition',
  latestAcquisition: 'latestAcquisition',
};

/**
 * @param {unknown} value
 * @param {number} [fallback=0]
 * @returns {number}
 */
export function readNonNegativeFiniteNumber(value, fallback = 0) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return fallback;
  }
  return value;
}

/**
 * @param {unknown} metrics
 * @returns {Array<Record<string, unknown>>}
 */
export function readByHotspotMetrics(metrics) {
  if (!metrics || typeof metrics !== 'object') {
    return [];
  }
  const byHotspot = metrics.byHotspot;
  if (!Array.isArray(byHotspot)) {
    return [];
  }
  return byHotspot
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      entityType: typeof row.entityType === 'string' ? row.entityType : '',
      entityId: typeof row.entityId === 'string' ? row.entityId : '',
      name: typeof row.name === 'string' ? row.name : '',
      city: typeof row.city === 'string' ? row.city : '',
      state: typeof row.state === 'string' ? row.state : '',
      lat: typeof row.lat === 'number' && Number.isFinite(row.lat) ? row.lat : null,
      lng: typeof row.lng === 'number' && Number.isFinite(row.lng) ? row.lng : null,
      userCount: readNonNegativeFiniteNumber(row.userCount),
      earliestAcquisition:
        typeof row.earliestAcquisition === 'string' ? row.earliestAcquisition : '',
      latestAcquisition:
        typeof row.latestAcquisition === 'string' ? row.latestAcquisition : '',
      hasCoordinates:
        typeof row.lat === 'number'
        && Number.isFinite(row.lat)
        && typeof row.lng === 'number'
        && Number.isFinite(row.lng),
    }));
}

/**
 * @param {unknown} metrics
 * @returns {{ usersWithFirstTouch: number, usersMissingCoordinates: number, hotspotCount: number }}
 */
export function readFirstTouchTotals(metrics) {
  const totals =
    metrics && typeof metrics === 'object' && metrics.totals && typeof metrics.totals === 'object'
      ? metrics.totals
      : {};
  return {
    usersWithFirstTouch: readNonNegativeFiniteNumber(totals.usersWithFirstTouch),
    usersMissingCoordinates: readNonNegativeFiniteNumber(totals.usersMissingCoordinates),
    hotspotCount: readNonNegativeFiniteNumber(totals.hotspotCount),
  };
}

/**
 * @param {Array<Record<string, unknown>>} rows
 * @returns {Array<{ location: { lat: number, lng: number }, weight: number }>}
 */
export function buildHeatmapWeightedLocations(rows) {
  return rows
    .filter((row) => row.hasCoordinates && row.userCount > 0)
    .map((row) => ({
      location: { lat: row.lat, lng: row.lng },
      weight: row.userCount,
    }));
}

/**
 * @param {string} isoDate
 * @returns {string}
 */
export function formatAcquisitionDate(isoDate) {
  if (!isoDate) {
    return '—';
  }
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return parsed.toISOString().slice(0, 10);
}

/**
 * @param {string} entityType
 * @returns {string}
 */
export function formatEntityTypeLabel(entityType) {
  if (entityType === 'event') {
    return 'Event';
  }
  if (entityType === 'brewery') {
    return 'Brewery';
  }
  return entityType || 'Unknown';
}
